import { FormLabel } from '@components/shared/form-label.component';
import { Button } from '@components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { cn } from '@lib/utils';
import { BaseSelectOption, SelectOption, mapToSelectOptions } from '@utils/select.util';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Separator } from '../ui/separator';

type PopoverSelectorProps = {
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
  emptyMessage?: string;
  formatValue?: (option: SelectOption) => string;
  hasError?: boolean;
  isRequired?: boolean;
  label?: string;
  name?: string;
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  onClear?: () => void;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  options: Array<BaseSelectOption>;
  placeholder?: string;
  renderOption?: (option: SelectOption) => React.ReactNode;
  searchPlaceholder?: string;
  setValue?: (value: string) => void;

  triggerClassName?: string;
  value: string | null | undefined;
};

export const PopoverSelector = (props: PopoverSelectorProps) => {
  const {
    className,
    contentClassName,
    disabled = false,
    emptyMessage = 'No options found',
    formatValue,
    hasError,
    isRequired = false,
    label,
    name,
    onBlur,
    onClear,
    onChange,
    options,
    placeholder = 'Select an option',
    renderOption,
    searchPlaceholder = 'Search...',
    setValue,
    triggerClassName,
    value,
  } = props;
  const ariaInvalid = hasError;

  const [open, setOpen] = useState(false);

  const normalizedOptions = useMemo(() => mapToSelectOptions(options), [options]);

  const handleSelect = (id: string) => {
    const selectedOption = normalizedOptions.find((option) => option.value === id);
    if (selectedOption?.disabled) {
      return;
    }

    if (onChange && name) {
      const syntheticEvent = {
        target: {
          name,
          value: id,
        },
      } as React.ChangeEvent<HTMLSelectElement>;
      onChange(syntheticEvent);
    } else if (setValue) {
      setValue(id);
    }
    setOpen(false);
  };

  const selectedOption = value ? normalizedOptions.find((option) => option.value === value) : null;
  const displayValue = selectedOption
    ? formatValue
      ? formatValue(selectedOption)
      : selectedOption.label
    : placeholder;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <FormLabel
          label={label}
          isRequired={isRequired}
        />
      )}
      <div className={cn('flex w-full flex-row gap-1')}>
        <Popover
          open={open}
          onOpenChange={setOpen}
        >
          <PopoverTrigger asChild>
            <Button
              aria-invalid={ariaInvalid}
              aria-expanded={open}
              className={cn(
                'h-10 w-full min-w-0 justify-between rounded-md border font-normal bg-gray-100 px-3 py-2 text-base md:text-sm',
                selectedOption ? 'text-gray-600 hover:text-gray-600' : 'text-gray-400 hover:text-gray-400',
                'focus-visible:border-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300',
                'aria-invalid:border-red-500 aria-invalid:focus-visible:border-red-500 aria-invalid:focus-visible:ring-red-200',
                'disabled:cursor-not-allowed disabled:opacity-50',
                'border-gray-200',
                triggerClassName,
              )}
              disabled={disabled}
              id={name}
              name={name}
              onBlur={onBlur}
              role="combobox"
              variant="outline"
            >
              <span className="truncate">{displayValue}</span>
              <div className="flex shrink-0 items-center gap-2">
                {value && onClear && (
                  <>
                    <div
                      role="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onClear();
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      className="text-gray-400 hover:text-red-500 mr-2 cursor-pointer relative z-10 pointer-events-auto"
                      style={{ pointerEvents: 'auto' }}
                      aria-label="Clear selection"
                    >
                      <X className="size-4 pointer-events-none" />
                    </div>
                    <Separator
                      orientation="vertical"
                      className="h-6 mr-2"
                    />
                  </>
                )}
                <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent className={cn('p-0', contentClassName)}>
            <Command>
              <CommandInput placeholder={searchPlaceholder} />
              <CommandList>
                <CommandEmpty>{emptyMessage}</CommandEmpty>
                <CommandGroup>
                  {normalizedOptions
                    .filter((option) => !option.hidden || value === option.value)
                    .map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.label}
                        keywords={[option.value]}
                        onSelect={() => handleSelect(option.value)}
                        className={cn(option.disabled && 'opacity-50 cursor-not-allowed')}
                        disabled={option.disabled}
                      >
                        <Check className={cn('mr-2 h-4 w-4', value === option.value ? 'opacity-100' : 'opacity-0')} />
                        <div className="flex min-w-0 flex-1 items-center">
                          {renderOption ? renderOption(option) : option.label}
                        </div>
                      </CommandItem>
                    ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
